import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Slider,
  Tooltip
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Preview,
  Add,
  Remove,
  ExpandMore,
  AttachMoney,
  Schedule,
  Group,
  Storage,
  Security,
  Support,
  Analytics,
  CloudSync,
  Api,
  Notifications,
  Check,
  Close,
  Info,
  Warning,
  Error
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface PricingTier {
  id: string;
  name: string;
  description: string;
  price: number;
  billingCycle: 'monthly' | 'yearly';
  features: string[];
  limits: {
    users: number;
    projects: number;
    storage: number; // GB
    apiCalls: number;
  };
  isPopular: boolean;
  isCustom: boolean;
}

interface Feature {
  id: string;
  name: string;
  description: string;
  category: string;
  isCore: boolean;
  isPremium: boolean;
}

interface SubscriptionFormData {
  name: string;
  description: string;
  category: string;
  status: 'draft' | 'active' | 'inactive';
  billingModel: 'subscription' | 'usage' | 'hybrid';
  trialPeriod: number; // days
  setupFee: number;
  currency: string;
  taxRate: number;
  pricingTiers: PricingTier[];
  features: Feature[];
  terms: {
    cancellationPolicy: string;
    refundPolicy: string;
    upgradePolicy: string;
    downgradePolicy: string;
  };
  integrations: {
    paymentGateways: string[];
    billingSystem: string;
    crmSystem: string;
  };
  notifications: {
    welcomeEmail: boolean;
    billingReminders: boolean;
    usageAlerts: boolean;
    renewalNotices: boolean;
  };
  analytics: {
    trackUsage: boolean;
    generateReports: boolean;
    customMetrics: string[];
  };
}

interface FormErrors {
  [key: string]: string;
}

const SubscriptionCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPreview, setShowPreview] = useState(false);

  const [formData, setFormData] = useState<SubscriptionFormData>({
    name: '',
    description: '',
    category: '',
    status: 'draft',
    billingModel: 'subscription',
    trialPeriod: 0,
    setupFee: 0,
    currency: 'USD',
    taxRate: 0,
    pricingTiers: [],
    features: [],
    terms: {
      cancellationPolicy: '',
      refundPolicy: '',
      upgradePolicy: '',
      downgradePolicy: ''
    },
    integrations: {
      paymentGateways: [],
      billingSystem: '',
      crmSystem: ''
    },
    notifications: {
      welcomeEmail: false,
      billingReminders: false,
      usageAlerts: false,
      renewalNotices: false
    },
    analytics: {
      trackUsage: false,
      generateReports: false,
      customMetrics: []
    }
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleNestedInputChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof SubscriptionFormData] as any,
        [field]: value
      }
    }));
  };

  const addPricingTier = () => {
    const newTier: PricingTier = {
      id: Date.now().toString(),
      name: '',
      description: '',
      price: 0,
      billingCycle: 'monthly',
      features: [],
      limits: {
        users: 1,
        projects: 1,
        storage: 1,
        apiCalls: 100
      },
      isPopular: false,
      isCustom: false
    };
    setFormData(prev => ({
      ...prev,
      pricingTiers: [...prev.pricingTiers, newTier]
    }));
  };

  const removePricingTier = (id: string) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.filter(tier => tier.id !== id)
    }));
  };

  const updatePricingTier = (id: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.map(tier =>
        tier.id === id ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const updateTierLimit = (tierId: string, limitField: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.map(tier =>
        tier.id === tierId
          ? {
              ...tier,
              limits: { ...tier.limits, [limitField]: value }
            }
          : tier
      )
    }));
  };

  const addFeature = () => {
    const newFeature: Feature = {
      id: Date.now().toString(),
      name: '',
      description: '',
      category: 'core',
      isCore: false,
      isPremium: false
    };
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, newFeature]
    }));
  };

  const removeFeature = (id: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(feature => feature.id !== id)
    }));
  };

  const updateFeature = (id: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map(feature =>
        feature.id === id ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const addTierFeature = (tierId: string, feature: string) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.map(tier =>
        tier.id === tierId
          ? { ...tier, features: [...tier.features, feature] }
          : tier
      )
    }));
  };

  const removeTierFeature = (tierId: string, featureIndex: number) => {
    setFormData(prev => ({
      ...prev,
      pricingTiers: prev.pricingTiers.map(tier =>
        tier.id === tierId
          ? {
              ...tier,
              features: tier.features.filter((_, index) => index !== featureIndex)
            }
          : tier
      )
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Subscription name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.pricingTiers.length === 0) {
      newErrors.pricingTiers = 'At least one pricing tier is required';
    }

    formData.pricingTiers.forEach((tier, index) => {
      if (!tier.name.trim()) {
        newErrors[`tier_${index}_name`] = 'Tier name is required';
      }
      if (tier.price < 0) {
        newErrors[`tier_${index}_price`] = 'Price cannot be negative';
      }
    });

    if (formData.trialPeriod < 0) {
      newErrors.trialPeriod = 'Trial period cannot be negative';
    }

    if (formData.taxRate < 0 || formData.taxRate > 100) {
      newErrors.taxRate = 'Tax rate must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      navigate('/subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const calculateAnnualPrice = (monthlyPrice: number) => {
    return Math.round(monthlyPrice * 12 * 0.8); // 20% discount for annual
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/subscriptions')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Create New Subscription Plan
        </Typography>
      </Box>

      {/* Status Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure your subscription plan with pricing tiers, features, and billing options.
          You can preview the plan before publishing.
        </Typography>
      </Alert>

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'grid', gap: 3 }}>
          {/* Basic Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Subscription Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="construction">Construction</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                  <MenuItem value="startup">Startup</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                multiline
                rows={3}
                fullWidth
                sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}
              />
            </Box>
          </Paper>

          {/* Billing Configuration */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Billing Configuration
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Billing Model</InputLabel>
                <Select
                  value={formData.billingModel}
                  onChange={(e) => handleInputChange('billingModel', e.target.value)}
                  label="Billing Model"
                >
                  <MenuItem value="subscription">Subscription</MenuItem>
                  <MenuItem value="usage">Usage-based</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  label="Currency"
                >
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                  <MenuItem value="CAD">CAD (C$)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Trial Period (days)"
                type="number"
                value={formData.trialPeriod}
                onChange={(e) => handleInputChange('trialPeriod', parseInt(e.target.value) || 0)}
                error={!!errors.trialPeriod}
                helperText={errors.trialPeriod}
                fullWidth
              />
              <TextField
                label="Setup Fee"
                type="number"
                value={formData.setupFee}
                onChange={(e) => handleInputChange('setupFee', parseFloat(e.target.value) || 0)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                fullWidth
              />
            </Box>
          </Paper>

          {/* Pricing Tiers */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Pricing Tiers
              </Typography>
              <Button
                startIcon={<Add />}
                onClick={addPricingTier}
                variant="outlined"
              >
                Add Tier
              </Button>
            </Box>
            {errors.pricingTiers && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.pricingTiers}
              </Alert>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
              {formData.pricingTiers.map((tier, index) => (
                <Box key={tier.id}>
                  <Card sx={{ height: '100%', position: 'relative' }}>
                    {tier.isPopular && (
                      <Chip
                        label="Most Popular"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1
                        }}
                      />
                    )}
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <TextField
                          label="Tier Name"
                          value={tier.name}
                          onChange={(e) => updatePricingTier(tier.id, 'name', e.target.value)}
                          error={!!errors[`tier_${index}_name`]}
                          helperText={errors[`tier_${index}_name`]}
                          size="small"
                          sx={{ flexGrow: 1, mr: 1 }}
                        />
                        <IconButton
                          onClick={() => removePricingTier(tier.id)}
                          size="small"
                          color="error"
                        >
                          <Remove />
                        </IconButton>
                      </Box>
                      
                      <TextField
                        label="Description"
                        value={tier.description}
                        onChange={(e) => updatePricingTier(tier.id, 'description', e.target.value)}
                        multiline
                        rows={2}
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                        <TextField
                          label="Price"
                          type="number"
                          value={tier.price}
                          onChange={(e) => updatePricingTier(tier.id, 'price', parseFloat(e.target.value) || 0)}
                          error={!!errors[`tier_${index}_price`]}
                          helperText={errors[`tier_${index}_price`]}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                          size="small"
                        />
                        <FormControl size="small">
                          <InputLabel>Billing Cycle</InputLabel>
                          <Select
                            value={tier.billingCycle}
                            onChange={(e) => updatePricingTier(tier.id, 'billingCycle', e.target.value)}
                            label="Billing Cycle"
                          >
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {tier.billingCycle === 'monthly' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Annual: ${calculateAnnualPrice(tier.price)}/year (20% off)
                        </Typography>
                      )}

                      <Typography variant="subtitle2" gutterBottom>
                        Limits
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                        <TextField
                          label="Users"
                          type="number"
                          value={tier.limits.users}
                          onChange={(e) => updateTierLimit(tier.id, 'users', parseInt(e.target.value) || 0)}
                          size="small"
                        />
                        <TextField
                          label="Projects"
                          type="number"
                          value={tier.limits.projects}
                          onChange={(e) => updateTierLimit(tier.id, 'projects', parseInt(e.target.value) || 0)}
                          size="small"
                        />
                        <TextField
                          label="Storage (GB)"
                          type="number"
                          value={tier.limits.storage}
                          onChange={(e) => updateTierLimit(tier.id, 'storage', parseInt(e.target.value) || 0)}
                          size="small"
                        />
                        <TextField
                          label="API Calls"
                          type="number"
                          value={tier.limits.apiCalls}
                          onChange={(e) => updateTierLimit(tier.id, 'apiCalls', parseInt(e.target.value) || 0)}
                          size="small"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={tier.isPopular}
                              onChange={(e) => updatePricingTier(tier.id, 'isPopular', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Popular"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={tier.isCustom}
                              onChange={(e) => updatePricingTier(tier.id, 'isCustom', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Custom"
                        />
                      </Box>

                      <Typography variant="subtitle2" gutterBottom>
                        Features
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        {tier.features.map((feature, featureIndex) => (
                          <Chip
                            key={featureIndex}
                            label={feature}
                            onDelete={() => removeTierFeature(tier.id, featureIndex)}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                      <TextField
                        label="Add Feature"
                        size="small"
                        fullWidth
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const target = e.target as HTMLInputElement;
                            if (target.value.trim()) {
                              addTierFeature(tier.id, target.value.trim());
                              target.value = '';
                            }
                          }
                        }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Features Management */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Features Library
              </Typography>
              <Button
                startIcon={<Add />}
                onClick={addFeature}
                variant="outlined"
              >
                Add Feature
              </Button>
            </Box>
            <Box sx={{ display: 'grid', gap: 2 }}>
              {formData.features.map((feature) => (
                <Card key={feature.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 1fr auto' }, gap: 2, alignItems: 'center' }}>
                      <TextField
                        label="Feature Name"
                        value={feature.name}
                        onChange={(e) => updateFeature(feature.id, 'name', e.target.value)}
                        size="small"
                      />
                      <TextField
                        label="Description"
                        value={feature.description}
                        onChange={(e) => updateFeature(feature.id, 'description', e.target.value)}
                        size="small"
                      />
                      <FormControl size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={feature.category}
                          onChange={(e) => updateFeature(feature.id, 'category', e.target.value)}
                          label="Category"
                        >
                          <MenuItem value="core">Core</MenuItem>
                          <MenuItem value="analytics">Analytics</MenuItem>
                          <MenuItem value="integration">Integration</MenuItem>
                          <MenuItem value="mobile">Mobile</MenuItem>
                          <MenuItem value="security">Security</MenuItem>
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={feature.isCore}
                              onChange={(e) => updateFeature(feature.id, 'isCore', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Core"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={feature.isPremium}
                              onChange={(e) => updateFeature(feature.id, 'isPremium', e.target.checked)}
                              size="small"
                            />
                          }
                          label="Premium"
                        />
                        <IconButton
                          onClick={() => removeFeature(feature.id)}
                          size="small"
                          color="error"
                        >
                          <Remove />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>

          {/* Terms & Policies */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Terms & Policies
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Cancellation Policy"
                value={formData.terms.cancellationPolicy}
                onChange={(e) => handleNestedInputChange('terms', 'cancellationPolicy', e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Refund Policy"
                value={formData.terms.refundPolicy}
                onChange={(e) => handleNestedInputChange('terms', 'refundPolicy', e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Upgrade Policy"
                value={formData.terms.upgradePolicy}
                onChange={(e) => handleNestedInputChange('terms', 'upgradePolicy', e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
              <TextField
                label="Downgrade Policy"
                value={formData.terms.downgradePolicy}
                onChange={(e) => handleNestedInputChange('terms', 'downgradePolicy', e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </Paper>

          {/* Integrations */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Integrations
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Payment Gateways</InputLabel>
                <Select
                  multiple
                  value={formData.integrations.paymentGateways}
                  onChange={(e) => handleNestedInputChange('integrations', 'paymentGateways', e.target.value)}
                  label="Payment Gateways"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="stripe">Stripe</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="square">Square</MenuItem>
                  <MenuItem value="authorize">Authorize.Net</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Billing System</InputLabel>
                <Select
                  value={formData.integrations.billingSystem}
                  onChange={(e) => handleNestedInputChange('integrations', 'billingSystem', e.target.value)}
                  label="Billing System"
                >
                  <MenuItem value="stripe">Stripe Billing</MenuItem>
                  <MenuItem value="chargebee">Chargebee</MenuItem>
                  <MenuItem value="recurly">Recurly</MenuItem>
                  <MenuItem value="zuora">Zuora</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>CRM System</InputLabel>
                <Select
                  value={formData.integrations.crmSystem}
                  onChange={(e) => handleNestedInputChange('integrations', 'crmSystem', e.target.value)}
                  label="CRM System"
                >
                  <MenuItem value="salesforce">Salesforce</MenuItem>
                  <MenuItem value="hubspot">HubSpot</MenuItem>
                  <MenuItem value="pipedrive">Pipedrive</MenuItem>
                  <MenuItem value="zoho">Zoho CRM</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Notifications */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Settings
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.welcomeEmail}
                    onChange={(e) => handleNestedInputChange('notifications', 'welcomeEmail', e.target.checked)}
                  />
                }
                label="Welcome Email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.billingReminders}
                    onChange={(e) => handleNestedInputChange('notifications', 'billingReminders', e.target.checked)}
                  />
                }
                label="Billing Reminders"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.usageAlerts}
                    onChange={(e) => handleNestedInputChange('notifications', 'usageAlerts', e.target.checked)}
                  />
                }
                label="Usage Alerts"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.renewalNotices}
                    onChange={(e) => handleNestedInputChange('notifications', 'renewalNotices', e.target.checked)}
                  />
                }
                label="Renewal Notices"
              />
            </Box>
          </Paper>

          {/* Analytics */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Analytics & Reporting
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.analytics.trackUsage}
                      onChange={(e) => handleNestedInputChange('analytics', 'trackUsage', e.target.checked)}
                    />
                  }
                  label="Track Usage"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.analytics.generateReports}
                      onChange={(e) => handleNestedInputChange('analytics', 'generateReports', e.target.checked)}
                    />
                  }
                  label="Generate Reports"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Custom Metrics
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {formData.analytics.customMetrics.map((metric, index) => (
                    <Chip
                      key={index}
                      label={metric}
                      onDelete={() => {
                        const newMetrics = formData.analytics.customMetrics.filter((_, i) => i !== index);
                        handleNestedInputChange('analytics', 'customMetrics', newMetrics);
                      }}
                      size="small"
                    />
                  ))}
                </Box>
                <TextField
                  label="Add Custom Metric"
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const target = e.target as HTMLInputElement;
                      if (target.value.trim()) {
                        const newMetrics = [...formData.analytics.customMetrics, target.value.trim()];
                        handleNestedInputChange('analytics', 'customMetrics', newMetrics);
                        target.value = '';
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => setShowPreview(!showPreview)}
              startIcon={<Preview />}
            >
              {showPreview ? 'Hide Preview' : 'Preview'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/subscriptions')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<Save />}
            >
              {loading ? 'Creating...' : 'Create Subscription'}
            </Button>
          </Box>
        </Box>
      </form>

      {/* Preview Modal/Section */}
      {showPreview && (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Subscription Preview
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="h5">{formData.name}</Typography>
            <Typography variant="body1" color="text.secondary">
              {formData.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {formData.pricingTiers.map((tier) => (
                <Card key={tier.id} sx={{ minWidth: 250, position: 'relative' }}>
                  {tier.isPopular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                  )}
                  <CardContent>
                    <Typography variant="h6">{tier.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {tier.description}
                    </Typography>
                    <Typography variant="h4" color="primary">
                      ${tier.price}
                      <Typography component="span" variant="body2">
                        /{tier.billingCycle === 'monthly' ? 'mo' : 'yr'}
                      </Typography>
                    </Typography>
                    <List dense>
                      {tier.features.map((feature, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Check color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions>
                    <Button fullWidth variant="outlined">
                      Choose Plan
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SubscriptionCreate;